import React from 'react';
import FindVisitorForm from "../SubComponents/FindVisitorForm";
import CreateVisitorProfileForm from "../SubComponents/CreateVisitorProfileForm";
import { TAB_INDEX } from "../../External/Model/Model";
import VisitDetailsForm from "../SubComponents/VisitDetailsForm";
import RequestAccessSummary from "../SubComponents/RequestAccessSummary";
import SubmittingStep from "../../External/Documentation/SubComponents/SubmittingStep";
import FullPageLoader from "../../../../../utils/FullPageLoader";

/**
 * StepsRequestAccess
 *
 * Hook/Component to manage the rendering of different steps
 * for the QR Request Access flow. Supports three main flows:
 * 1. Find existing visitor
 * 2. Find existing visitor and create visitor
 * 3. I am new here
 *
 * Each flow renders the appropriate form components according
 * to the current active step.
 *
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param {Function} t - Translation function from react-i18next
 * @param {Function} navigate - React Router navigate function
 * @param {Function} setSteps - Function to update steps state
 * @param {Array} visitorList - Array of selected visitors
 * @param {Function} setVisitorList - Function to update visitorList
 * @param {Array} employeeList - Array of selected employees
 * @param {Function} setEmployeeList - Function to update employeeList
 * @param {Boolean} emptyFields - Flag indicating empty required fields
 * @param {Number} activeStep - Currently active step index
 * @param {Function} setActiveStep - Function to update active step
 * @param {Array} visitorOptions - Options for visitors autocomplete
 * @param {Array} employeeOptions - Options for employees autocomplete
 * @param {Object} requestAccessItem - Current request access model
 * @param {Object} photoPickerFile - State of uploaded files
 * @param {Function} setPhotoPickerFile - Function to update photoPickerFile
 * @param {Boolean} showLoader - Flag to show loader
 * @param {Function} handleChange - Handler for form input changes
 * @param {Object} userData - Current user data
 * @param {Function} setUserData - Function to update userData
 * @param {Function} handleChangeVisitor - Handler for visitor input changes
 * @param {Boolean} emptyFieldsVisitor - Flag for empty visitor fields
 * @param {Boolean} isValidEmail - Flag indicating valid email
 * @param {Boolean} isValidPhoneNumber - Flag indicating valid phone number
 * @param {Function} handleEmailChange - Handler for email changes
 * @param {Function} handlePhoneNumberChange - Handler for phone number changes
 *
 * @returns {Object} An object containing rendering functions for each flow:
 * - renderFindBySelfSteps
 * - renderFindBySelfCreateVisitorSteps
 * - renderIAmNewHereSteps
 */
const StepsRequestAccess = ({
                                t, navigate, setSteps,
                                visitorList, setVisitorList,
                                employeeList, setEmployeeList,
                                emptyFields, activeStep, setActiveStep,
                                requestAccessItem, photoPickerFile, setPhotoPickerFile,
                                showLoader, handleChange,
                                userData, setUserData,
                                handleChangeVisitor, emptyFieldsVisitor,
                                isValidEmail, isValidPhoneNumber,
                                handleEmailChange, handlePhoneNumberChange
                            }) => {

    /**
     * Render steps for the "Find by Self" flow
     * @returns {JSX.Element|null}
     */
    const renderFindBySelfSteps = () => {
        switch (activeStep) {
            case TAB_INDEX.ONE:
                return <FindVisitorForm
                    t={t}
                    navigate={navigate}
                    visitorList={visitorList}
                    setVisitorList={setVisitorList}
                    emptyFields={emptyFields}
                />;
            case TAB_INDEX.TWO:
                return (
                    <VisitDetailsForm
                        t={t}
                        employeeList={employeeList}
                        setEmployeeList={setEmployeeList}
                        emptyFields={emptyFields}
                        requestAccessItem={requestAccessItem}
                        photoPickerFile={photoPickerFile}
                        setPhotoPickerFile={setPhotoPickerFile}
                        handleChange={handleChange}
                    />
                );
            case TAB_INDEX.THREE:
                return (
                    showLoader ? <FullPageLoader className="background-linear-gradient-950-opacity025"/>
                        :
                        <RequestAccessSummary
                            t={t}
                            visitorList={visitorList}
                            employeeList={employeeList}
                            emptyFields={emptyFields}
                            requestAccessItem={requestAccessItem}
                            photoPickerFile={photoPickerFile}
                            userData={userData}
                        />
                );
            case TAB_INDEX.FOUR:
                return (
                    <SubmittingStep
                        title={t("thank_you_for_your_submission")}
                        subtitle={t("administration_will_review_your_request")}
                        showBtnNext={false}
                    />
                );
            default:
                return null;
        }
    };

    /**
     * Render steps for the "Find by Self and Create Visitor" flow
     * @returns {JSX.Element|null}
     */
    const renderFindBySelfCreateVisitorSteps = () => {
        switch (activeStep) {
            case TAB_INDEX.ONE:
                return <></>; // Not used in this flow
            case TAB_INDEX.TWO:
                return (
                    <CreateVisitorProfileForm
                        t={t}
                        visitorList={visitorList}
                        setVisitorList={setVisitorList}
                        emptyFields={emptyFields}
                        setActiveStep={setActiveStep}
                        requestAccessItem={requestAccessItem}

                        userData={userData}
                        setUserData={setUserData}
                        handleChangeVisitor={handleChangeVisitor}
                        handleChange={handleChange}
                        emptyFieldsVisitor={emptyFieldsVisitor}
                        isValidEmail={isValidEmail}
                        isValidPhoneNumber={isValidPhoneNumber}
                        handleEmailChange={handleEmailChange}
                        handlePhoneNumberChange={handlePhoneNumberChange}
                    />
                );
            case TAB_INDEX.THREE:
                return (
                    <VisitDetailsForm
                        t={t}
                        employeeList={employeeList}
                        setEmployeeList={setEmployeeList}
                        emptyFields={emptyFields}
                        requestAccessItem={requestAccessItem}
                        photoPickerFile={photoPickerFile}
                        setPhotoPickerFile={setPhotoPickerFile}
                        handleChange={handleChange}
                    />
                );
            case TAB_INDEX.FOUR:
                return (
                    showLoader ? <FullPageLoader className="background-linear-gradient-950-opacity025"/>
                        :
                        <RequestAccessSummary
                            t={t}
                            visitorList={visitorList}
                            employeeList={employeeList}
                            emptyFields={emptyFields}
                            requestAccessItem={requestAccessItem}
                            photoPickerFile={photoPickerFile}
                            userData={userData}
                        />
                );
            case TAB_INDEX.FIVE:
                return (
                    <SubmittingStep
                        title={t("thank_you_for_your_submission")}
                        subtitle={t("administration_will_review_your_request")}
                        showBtnNext={false}
                    />
                );
            default:
                return null;
        }
    };

    /**
     * Render steps for the "I Am New Here" flow
     * @returns {JSX.Element|null}
     */
    const renderIAmNewHereSteps = () => {
        switch (activeStep) {
            case TAB_INDEX.ONE:
                return (
                    <CreateVisitorProfileForm
                        t={t}
                        visitorList={visitorList}
                        setVisitorList={setVisitorList}
                        emptyFields={emptyFields}
                        setActiveStep={setActiveStep}
                        requestAccessItem={requestAccessItem}

                        userData={userData}
                        setUserData={setUserData}
                        handleChangeVisitor={handleChangeVisitor}
                        handleChange={handleChange}
                        emptyFieldsVisitor={emptyFieldsVisitor}
                        isValidEmail={isValidEmail}
                        isValidPhoneNumber={isValidPhoneNumber}
                        handleEmailChange={handleEmailChange}
                        handlePhoneNumberChange={handlePhoneNumberChange}
                    />
                );
            case TAB_INDEX.TWO:
                return (
                    <VisitDetailsForm
                        t={t}
                        employeeList={employeeList}
                        setEmployeeList={setEmployeeList}
                        emptyFields={emptyFields}
                        requestAccessItem={requestAccessItem}
                        photoPickerFile={photoPickerFile}
                        setPhotoPickerFile={setPhotoPickerFile}
                        handleChange={handleChange}
                    />
                );
            case TAB_INDEX.THREE:
                return (
                    showLoader ? <FullPageLoader className="background-linear-gradient-950-opacity025"/>
                        :
                        <RequestAccessSummary
                            t={t}
                            visitorList={visitorList}
                            employeeList={employeeList}
                            emptyFields={emptyFields}
                            requestAccessItem={requestAccessItem}
                            photoPickerFile={photoPickerFile}
                            userData={userData}
                        />
                );
            case TAB_INDEX.FOUR:
                return (
                    <SubmittingStep
                        title={t("thank_you_for_your_submission")}
                        subtitle={t("administration_will_review_your_request")}
                        showBtnNext={false}
                    />
                );
            default:
                return null;
        }
    };

    return {
        renderFindBySelfSteps,
        renderFindBySelfCreateVisitorSteps,
        renderIAmNewHereSteps
    };
};

export default StepsRequestAccess;
